import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Home, Clock, AlertCircle, ArrowLeft } from 'lucide-react';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function NotFoundPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 py-12">
      <Helmet>
        <title>404 - Page Not Found | Clock Online</title>
        <meta name="description" content="The page you are looking for could not be found. Return to Clock Online homepage to access our free online alarm clock, timers, and more." />
        <link rel="canonical" href={getCanonicalUrl('/404')} />
        <meta property="og:title" content="404 - Page Not Found | Clock Online" />
        <meta property="og:description" content="The page you are looking for could not be found. Return to Clock Online homepage." />
        <meta property="og:url" content={getCanonicalUrl('/404')} />
        <meta name="twitter:title" content="404 - Page Not Found | Clock Online" />
        <meta name="twitter:description" content="The page you are looking for could not be found. Return to Clock Online homepage." />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-blue-600/30 p-8 md:p-12 shadow-2xl text-center">
          
          {/* 404 Icon and Title */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <AlertCircle className="w-12 h-12 text-cyan-400" />
              <h1 className="text-6xl md:text-8xl font-bold text-white">404</h1>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-cyan-300 mb-4">
              Page Not Found
            </h2>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              Sorry, the page you are looking for doesn't exist or has been moved. 
              Let's get you back on track.
            </p>
          </div>

          {/* Quick Links */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-cyan-300 mb-6">Popular Pages</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/"
                className="bg-white/10 hover:bg-white/20 border border-cyan-400/30 rounded-xl p-4 transition-all hover:scale-105 flex items-center justify-center gap-3 group"
              >
                <Home className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300" />
                <span className="text-white font-medium">Home</span>
              </Link>
              
              <Link
                to="/timers"
                className="bg-white/10 hover:bg-white/20 border border-cyan-400/30 rounded-xl p-4 transition-all hover:scale-105 flex items-center justify-center gap-3 group"
              >
                <Clock className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300" />
                <span className="text-white font-medium">Timers</span>
              </Link>
              
              <Link
                to="/video-alarm"
                className="bg-white/10 hover:bg-white/20 border border-cyan-400/30 rounded-xl p-4 transition-all hover:scale-105 flex items-center justify-center gap-3 group"
              >
                <span className="text-cyan-400 group-hover:text-cyan-300">▶</span>
                <span className="text-white font-medium">Video Alarm</span>
              </Link>
              
              <Link
                to="/radio-alarm"
                className="bg-white/10 hover:bg-white/20 border border-cyan-400/30 rounded-xl p-4 transition-all hover:scale-105 flex items-center justify-center gap-3 group"
              >
                <span className="text-cyan-400 group-hover:text-cyan-300">📻</span>
                <span className="text-white font-medium">Radio Alarm</span>
              </Link>
              
              <Link
                to="/meditation"
                className="bg-white/10 hover:bg-white/20 border border-cyan-400/30 rounded-xl p-4 transition-all hover:scale-105 flex items-center justify-center gap-3 group"
              >
                <span className="text-cyan-400 group-hover:text-cyan-300">🧘</span>
                <span className="text-white font-medium">Meditation</span>
              </Link>
              
              <Link
                to="/world-clock"
                className="bg-white/10 hover:bg-white/20 border border-cyan-400/30 rounded-xl p-4 transition-all hover:scale-105 flex items-center justify-center gap-3 group"
              >
                <span className="text-cyan-400 group-hover:text-cyan-300">🌍</span>
                <span className="text-white font-medium">World Clock</span>
              </Link>
            </div>
          </div>

          {/* Back Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back Home
            </Link>
            
            <button
              onClick={() => window.history.back()}
              className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium py-3 px-8 rounded-xl transition-all flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

